/**
 * Checkbox Component
 *
 * A checkbox supporting unchecked, checked, and indeterminate states,
 * each with default, hover, and disabled styling from the design system.
 *
 * @example
 * // Basic usage
 * <Checkbox />
 * <Checkbox checked />
 *
 * @example
 * // Controlled
 * const [checked, setChecked] = useState(false);
 * <Checkbox checked={checked} onCheckedChange={setChecked} />
 *
 * @example
 * // Rounded variant
 * <Checkbox variant="rounded" />
 *
 * @example
 * // Sizes (md is 20px, sm is 16px)
 * <Checkbox size="sm" />
 *
 * @example
 * // Indeterminate
 * <Checkbox checked="indeterminate" />
 *
 * @example
 * // Disabled
 * <Checkbox disabled />
 * <Checkbox disabled checked />
 */
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { ReactComponent as CheckIcon } from 'src/assets/tailwind/check.svg';
import { ReactComponent as MinusIcon } from 'src/assets/tailwind/minus.svg';
import { cn } from 'src/utils/utils';

import { Icon } from './icon';

const checkboxVariants = cva(
  `
    tw:group
    tw:grid
    tw:place-content-center
    tw:shrink-0
    tw:overflow-hidden

    tw:bg-primary

    tw:border-solid
    tw:border-border-primary

    tw:transition-all
    tw:duration-200
    tw:ease-in-out

    tw:hover:bg-primary-hover
    tw:hover:border-border-primary-hover

    tw:data-[state=checked]:bg-brand
    tw:data-[state=checked]:border-border-brand
    tw:data-[state=indeterminate]:bg-brand
    tw:data-[state=indeterminate]:border-border-brand

    tw:hover:data-[state=checked]:bg-brand-hover
    tw:hover:data-[state=checked]:border-border-brand-hover
    tw:hover:data-[state=indeterminate]:bg-brand-hover
    tw:hover:data-[state=indeterminate]:border-border-brand-hover

    tw:disabled:cursor-not-allowed
    tw:disabled:bg-disabled
    tw:disabled:border-border-disabled
    tw:disabled:hover:bg-disabled
    tw:disabled:hover:border-border-disabled

    tw:disabled:data-[state=checked]:bg-brand-disabled
    tw:disabled:data-[state=checked]:border-border-disabled-subtle
    tw:disabled:data-[state=indeterminate]:bg-brand-disabled
    tw:disabled:data-[state=indeterminate]:border-border-disabled-subtle
    tw:disabled:hover:data-[state=checked]:bg-brand-disabled
    tw:disabled:hover:data-[state=checked]:border-border-disabled-subtle
    tw:disabled:hover:data-[state=indeterminate]:bg-brand-disabled
    tw:disabled:hover:data-[state=indeterminate]:border-border-disabled-subtle

    tw:focus-visible:outline-none
    tw:focus-visible:ring-2
    tw:focus-visible:ring-brand
    tw:focus-visible:ring-offset-2
  `,
  {
    variants: {
      variant: {
        square: 'tw:rounded-s',
        rounded: 'tw:rounded-full'
      },
      size: {
        md: 'tw:w-l tw:h-l tw:border',
        sm: 'tw:w-m tw:h-m tw:border-[0.8px]'
      }
    },
    defaultVariants: {
      variant: 'square',
      size: 'md'
    }
  }
);

/** Glyph: md is 12x12, sm is 10x10 (no 10px step in the width/height scale). */
const checkboxIconVariants = cva('', {
  variants: {
    size: {
      md: 'tw:w-s tw:h-s',
      sm: 'tw:w-[10px] tw:h-[10px]'
    }
  },
  defaultVariants: {
    size: 'md'
  }
});

export interface CheckboxProps
  extends React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    VariantProps<typeof checkboxVariants> {}

const Checkbox = React.forwardRef<
  React.ElementRef<typeof CheckboxPrimitive.Root>,
  CheckboxProps
>(({ className, disabled, variant, size, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    disabled={disabled}
    className={cn(checkboxVariants({ variant, size }), className)}
    {...props}
  >
    <CheckboxPrimitive.Indicator
      className={cn(
        'tw:grid tw:place-content-center tw:transition-all tw:duration-200 tw:ease-in-out'
      )}
    >
      <Icon
        icon={props.checked === 'indeterminate' ? MinusIcon : CheckIcon}
        sizeClass={checkboxIconVariants({ size })}
        colorClass="tw:text-logo-over-dark"
      />
    </CheckboxPrimitive.Indicator>
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox, checkboxVariants };
